import React from 'react';
import { SlideWrapper, GlassCard, SectionLabel } from './SharedComponents';

export const ImpactStackDetailedSlide = ({ slide }) => {
  const layerColors = ['#0068ff', '#f59e0b', '#8b5cf6', '#06b6d4', '#22c55e'];
  const layerIcons = [
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>,
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8V7m0 10v1m9-9a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>,
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" /></svg>,
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
  ];

  return (
    <SlideWrapper slide={slide} background={
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#030308] via-[#060612] to-[#030308]" />
        <div className="absolute top-1/4 right-1/3 w-[600px] h-[600px] bg-[#0068ff] opacity-[0.06] blur-[180px] rounded-full" />
        <div className="absolute bottom-1/4 left-1/3 w-[500px] h-[500px] bg-[#8b5cf6] opacity-[0.04] blur-[150px] rounded-full" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#22c55e] opacity-[0.03] blur-[120px] rounded-full" />
      </div>
    }>
      <div className="relative z-10 flex items-center justify-center h-full px-6 py-4">
        {/* Left: Title + Insight */}
        <div className="flex-1 max-w-[340px] mr-10">
          <SectionLabel delay={0}>Architecture</SectionLabel>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mt-4 mb-2 animate-slide-up">{slide.title}</h2>
          <p className="text-white/35 text-sm mb-8 animate-fade-in" style={{ animationDelay: '0.05s' }}>{slide.subtitle}</p>

          <GlassCard delay={0.5} className="p-5 mb-5" style={{ borderColor: 'rgba(0,104,255,0.2)' }}>
            <div className="text-[10px] uppercase tracking-widest text-white/25 mb-2">Key Insight</div>
            <p className="text-white/55 text-sm leading-relaxed">{slide.insight}</p>
          </GlassCard>

          <GlassCard delay={0.6} className="px-5 py-3 text-center" style={{ borderColor: 'rgba(0,104,255,0.15)' }}>
            <span className="text-white/40 text-xs">{slide.closing[0]}</span><br />
            <span className="text-[#0068ff] text-xs font-semibold">{slide.closing[1]}</span>
          </GlassCard>
        </div>

        {/* Right: Stack Diagram */}
        <div className="w-[520px] shrink-0 flex flex-col gap-2.5">
          {slide.layers.map((layer, idx) => {
            const color = layerColors[idx];
            const isTop = idx === 0;
            return (
              <GlassCard key={idx} delay={0.12 + idx * 0.08} className="relative overflow-hidden group hover:scale-[1.01] transition-transform duration-300" style={{
                borderColor: `${color}${isTop ? '40' : '20'}`,
                borderLeftWidth: '4px',
                borderLeftColor: color,
                boxShadow: isTop ? `0 0 30px ${color}10` : 'none'
              }}>
                {/* Layer number background */}
                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[60px] font-black leading-none pointer-events-none select-none" style={{ color: `${color}08` }}>
                  {layer.num}
                </div>

                <div className="relative p-4 flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg shrink-0 flex items-center justify-center" style={{ background: `${color}15`, color }}>
                    {layerIcons[idx]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color }}>Layer {layer.num}</span>
                      <span className="text-white/15">|</span>
                      <span className="text-white font-bold text-sm">{layer.name}</span>
                    </div>
                    <p className="text-white/35 text-xs mb-2">{layer.description}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {layer.items.map((item, i) => (
                        <span key={i} className="text-[9px] px-2 py-0.5 rounded-full" style={{
                          color, background: `${color}08`, border: `1px solid ${color}15`
                        }}>{item}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </GlassCard>
            );
          })}

          {/* Arrow indicator */}
          <div className="flex justify-center mt-1 animate-fade-in" style={{ animationDelay: '0.6s' }}>
            <div className="flex items-center gap-2 text-white/20">
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 10l7-7m0 0l7 7m-7-7v18" /></svg>
              <span className="text-[10px] uppercase tracking-widest">Each layer builds on the one below</span>
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 10l7-7m0 0l7 7m-7-7v18" /></svg>
            </div>
          </div>
        </div>
      </div>
    </SlideWrapper>
  );
};
